/* eslint-disable no-nested-ternary */
import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PropTypes from 'prop-types'

import find from 'lodash/find'
import groupBy from 'lodash/groupBy'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import replace from 'lodash/replace'
import toNumber from 'lodash/toNumber'

import Autorenew from '@material-ui/icons/Autorenew'

import { useDialog } from '@smartcoop/dialog'
import I18n, { useT } from '@smartcoop/i18n'
import { nfeLogo, userEdit, arrowLeftSimple, arrowRightSimple } from '@smartcoop/icons'
import { searchCep } from '@smartcoop/services/apis/brasilApi'
import { getFiscalFigure, getNfeOrganizations } from '@smartcoop/services/apis/smartcoopApi/resources/nfe'
import { getUserStateRegistrations as getUserStateRegistrationsService } from '@smartcoop/services/apis/smartcoopApi/resources/stateRegistration'
import { useSnackbar } from '@smartcoop/snackbar'
import { NfeActions } from '@smartcoop/stores/nfe'
import { UserActions } from '@smartcoop/stores/user'
import { selectUser } from '@smartcoop/stores/user/selectorUser'
import { colors } from '@smartcoop/styles'
import Button from '@smartcoop/web-components/Button'
import Icon from '@smartcoop/web-components/Icon'
import Modal from '@smartcoop/web-components/Modal'
import ConfirmModal from '@smartcoop/web-components/Modal/ConfirmModal'
import Stepper from '@smartcoop/web-components/Stepper'
import NfeIntegratorForm from '@smartcoop/web-containers/forms/profile/NfeIntegratorForm/NfeIntegratorForm'
import { ButtonsContainer } from '@smartcoop/web-containers/layouts/AuthenticatedLayout/theme'
import { Title } from '@smartcoop/web-containers/layouts/GuestLayout/theme'
import LoadingModal from '@smartcoop/web-containers/modals/LoadingModal'

const NfeIntegratorModal = (props) => {
  const { id, open, handleClose } = props
  const formRef = useRef(null)
  const t = useT()
  const snackbar = useSnackbar()
  const dispatch = useDispatch()
  const user = useSelector(selectUser)
  const { removeDialog, createDialog } = useDialog()

  const [step, setStep] = useState(user?.activeNfe ? 4 : 1)
  const [productsToIntegrate, setProductsToIntegrate] = useState([])
  const [customersToIntegrate, setCustomersToIntegrate] = useState([])
  const [fiscalFigures, setFiscalFigures] = useState([])
  const [inputSearch, setInputSearch] = useState(null)
  const [organizations, setOrganizations] = useState([])
  const [stateRegistrations, setStateRegistrations] = useState([])
  const [Inscricao, setInscricao] = useState([])
  const [formData, setFormData] = useState(null)

  const isUserIntegrationStep = useMemo(() => step === 3, [step])

  const blockSave = useMemo(() => find(Inscricao, item =>
    isEmpty(item?.Nome)
      || isEmpty(item?.Cep)
      || isEmpty(item?.Cidade)
      || isEmpty(item?.Uf)
      || isEmpty(item?.Bairro)
      || isEmpty(item?.Logradouro)
      || isEmpty(item?.NroEnd)
  ), [Inscricao])

  const onSuccess = useCallback((nextStep = true, response = []) => {
    if(nextStep) {
      setStep(step + 1)
      dispatch(UserActions.loadUser())
    }
    if(!isEmpty(response)) {
      snackbar.error(t('integration failed for {this} {items}', { this: response?.length, items: t('item', { howMany: response?.length }) }))
    } else {
      snackbar.success(t('integration succeed'))
    }
    removeDialog({
      id: 'loading'
    })
  })

  const loadFiscalFigures = useCallback(
    async () => {
      const { data } = await getFiscalFigure()
      setFiscalFigures(groupBy(data, 'organizationId'))
    },
    []
  )

  const loadOrganizations = useCallback(
    async () => {
      const { data: { data } } = await getNfeOrganizations({ nfe: true })
      setOrganizations(map(data, item => item?.nfe ? ({ ...item, subsidiaries: [item, ...item?.subsidiaries] }) : item ))
    },
    []
  )

  const loadStateRegistrations = useCallback(
    async () => {
      const { data: { data } } = await getUserStateRegistrationsService()

      setStateRegistrations(data)
    },
    []
  )

  useEffect(() => {
    dispatch(NfeActions.loadProducer())
    loadStateRegistrations()
    loadFiscalFigures()
    loadOrganizations()
  }, [dispatch, loadFiscalFigures, loadOrganizations, loadStateRegistrations])

  const integrateProduct = useCallback(async () => {
    createDialog({
      id: 'confirm-submit-nfe-integration-control',
      Component: ConfirmModal,
      props: {
        onConfirm: () => {
          createDialog({
            id: 'loading',
            Component: LoadingModal,
            props: {
              message: t('integrating smartcoop nfe')
            }
          })
          dispatch(NfeActions.integrationControl(
            productsToIntegrate,
            (data) => {
              setProductsToIntegrate(map(data, item => ({ cfopId: item?.data?.cfopId, nfeFiscalFigureId: item?.data?.nfeFiscalFigureId, productId: item?.data?.productId })))
              onSuccess(false, data)
              loadFiscalFigures()
            },
            () => {
              removeDialog({ id: 'loading' })
            }
          ))
        },
        textWarning: t('are you sure you want to register this integration?')
      }
    })
  }, [createDialog, dispatch, loadFiscalFigures, onSuccess, productsToIntegrate, removeDialog, t])

  const integrateClientes = useCallback(async () => {
    createDialog({
      id: 'confirm-submit-nfe-integration-clientes',
      Component: ConfirmModal,
      props: {
        onConfirm: () => {
          createDialog({
            id: 'loading',
            Component: LoadingModal,
            props: {
              message: t('integrating smartcoop nfe')
            }
          })
          dispatch(NfeActions.integrationControl(
            customersToIntegrate,
            (data) => {
              setCustomersToIntegrate(map(data, item => ({ organizationId: item?.data?.organizationId })))
              onSuccess(false, data)
              loadFiscalFigures()
              loadOrganizations()
            },
            () => {
              removeDialog({ id: 'loading' })
            }
          ))
        },
        textWarning: t('are you sure you want to register this integration?')
      }
    })
  }, [createDialog, customersToIntegrate, dispatch, loadFiscalFigures, loadOrganizations, onSuccess, removeDialog, t])

  const handleSubmitNfeIntegrator = useCallback(
    async (data) => {
      const currentCity = await searchCep(replace(data?.CEP, '-', ''))

      let updatedStateRegistrationData = []

      const func = map([...Inscricao], async item => {
        const currentStateRegistrationCity = await searchCep(replace(item?.Cep, '-', ''))

        return {
          ...item,
          Cidade: currentStateRegistrationCity?.ibge,
          Cep: toNumber(replace(item?.Cep, '-', ''))
        }
      })

      await Promise.all(func).then((result) => {
        updatedStateRegistrationData = result
      })

      const updatedData = {
        ...data,
        Cidade: currentCity?.ibge,
        CEP: toNumber(replace(data?.CEP, '-', '')),
        Inscricao: updatedStateRegistrationData
      }

      createDialog({
        id: 'confirm-submit-nfe-integrator-form',
        Component: ConfirmModal,
        props: {
          onConfirm: () => {
            createDialog({
              id: 'loading',
              Component: LoadingModal,
              props: {
                message: t('integrating smartcoop nfe')
              }
            })
            dispatch(NfeActions.integrateNfe(
              updatedData,
              () => {
                onSuccess()
                removeDialog({ id: 'loading' })
              },
              () => {
                removeDialog({ id: 'loading' })
              }
            ))
          },
          textWarning: t('are you sure you want to register this integration?')
        }
      })
    },
    [Inscricao, createDialog, dispatch, onSuccess, removeDialog, t]
  )

  return (
    <Modal
      id={ id }
      open={ open }
      disableEsc
      title={ <div style={ { marginTop: '10px', display: 'flex', flexDirection: 'column' } }>
        {step >= 4 && (
          <div style={ { display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', flex: 1, fontSize: 12, lineHeight: 1 } }>
            <Icon icon={ nfeLogo } size="15vh" style={ { marginRight: 25 } }/>
            <div style={ { display: 'flex', flexDirection: 'column' } }>
              <b style={ { marginBottom: 10 } }><span style={ { fontSize: 18 } }>✔</span> Usuário habilitado para uso do SmartCoop NF-e.</b>
              <span style={ { marginBottom: 5 } }>Baixe o aplicativo SmartCoop NF-e nas lojas da Play Store ou Apple Store.</span>
              <span style={ { marginBottom: 5 } }>Na WEB acesse: <a href="https://nfe.smart.coop.br">https://nfe.smart.coop.br</a></span>
              <span>Verifique em seu e-mail as instruções de acesso ao aplicativo SmartCoop NF-e.</span>
            </div>
            <Button
              id="go-back"
              onClick={ () => setStep(2) }
              variant="outlined"
              style={ { marginLeft: 30, marginRight: 30 } }
            >
              <Icon icon={ userEdit } size={ 18 } style={ { marginRight: '5px' } }/>
              {t('update my data')}
            </Button>
          </div>
        )}
        <Stepper step={ step === 4 ? 1 : step === 5 ? 2 : step } steps={ step < 4 ? 3 : 2 } />
        <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center' } }>
          <Title style={ { marginRight: '5px' } }>
            {t('nfe integrator')}
          </Title>
          { step === 2 &&
          `- ${ t('user register data') }`
          }
          { step === 3 &&
          `- ${ t('state registration') }`
          }
          { step === 4 &&
          `- ${ t('recommended products') }`
          }
          { step === 5 &&
          `- ${ t('customers') }`
          }
        </div>
      </div> }
      disableFullScreen
      maxWidth='lg'
      fullWidth
      contentContainerStyle={ { overflow: 'hidden' } }
      FooterComponent={ () => (
        <div style={ { display: 'flex', flex: 1, width: '100%' } }>
          {step >= 4 && (!isEmpty(productsToIntegrate) || !isEmpty(customersToIntegrate)) ? (
            <ButtonsContainer style={ { flex: 1 } }>
              <Button
                id="integrate"
                onClick={ step === 4 ? integrateProduct : integrateClientes }
                style={ { flex: 1 } }
                color="secondary"
              >
                <Autorenew size={ 30 } style={ { color: colors.black, marginRight: 5 } } />
                {`${ t('integrate') }`}
              </Button>
            </ButtonsContainer>
          ) : null }
          {step >= 4 && (isEmpty(productsToIntegrate) && isEmpty(customersToIntegrate)) && (
            <ButtonsContainer style={ { flex: 1 } }>
              <Button
                id="close-nfe-integrator"
                onClick={ () => {
                  setInputSearch(null)
                  setStep(step - 1)
                } }
                style={ { flex: 1, marginRight: 5 } }
                variant="outlined"
                disabled={ step === 4 }
              >
                <Icon icon={ arrowLeftSimple } size={ 14 } style={ { marginRight: 25 } }/>
                <I18n>products</I18n>
              </Button>
              <Button
                id="close-nfe-integrator"
                onClick={ () => {
                  setInputSearch(null)
                  setStep(step + 1)
                } }
                style={ { flex: 1 } }
                variant="outlined"
                disabled={ step === 5 }
              >
                <I18n>customers</I18n>
                <Icon icon={ arrowRightSimple } size={ 14 } style={ { marginLeft: 25 } }/>
              </Button>
            </ButtonsContainer>
          )}
          {step < 4 && (
            <ButtonsContainer style={ { flex: 1 } }>
              <Button
                id="cancel-nfe-integrator"
                onClick={ step === 1 ? handleClose : () => setStep(step - 1) }
                style={ { flex: 1, marginRight: 10 } }
                variant="outlined"
              >
                <I18n>{step === 1 ? 'cancel' : 'go back'}</I18n>
              </Button>
              <Button
                id="submit-nfe-integrator"
                // eslint-disable-next-line no-nested-ternary
                onClick={ step === 2 ? () => formRef.current.submit() : (isUserIntegrationStep ? () => handleSubmitNfeIntegrator(formData) : () => setStep(step + 1))  }
                style={ { flex: 1 } }
                disabled={ isUserIntegrationStep && (blockSave || isEmpty(Inscricao)) }
              >
                <I18n>{isUserIntegrationStep ? 'confirm' : 'next'}</I18n>
              </Button>
            </ButtonsContainer>
          )}
        </div>
      ) }
    >
      <NfeIntegratorForm
        ref={ formRef }
        handleClose={ handleClose }
        onSuccess={ onSuccess }
        setStep={ setStep }
        step={ step }
        setProductsToIntegrate={ setProductsToIntegrate }
        productsToIntegrate={ productsToIntegrate }
        customersToIntegrate={ customersToIntegrate }
        setCustomersToIntegrate={ setCustomersToIntegrate }
        setFiscalFigures={ setFiscalFigures }
        fiscalFigures={ fiscalFigures }
        setFormData={ setFormData }
        formData={ formData }
        inputSearch={ inputSearch }
        setInputSearch={ setInputSearch }
        organizations={ organizations }
        setOrganizations={ setOrganizations }
        stateRegistrations={ stateRegistrations }
        setStateRegistrations={ setStateRegistrations }
        Inscricao={ Inscricao }
        setInscricao={ setInscricao }
        loadStateRegistrations={ loadStateRegistrations }
      />
    </Modal>
  )
}

NfeIntegratorModal.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired
}

NfeIntegratorModal.defaultProps = {
}

export default NfeIntegratorModal
